import { API } from 'components/CommonFunc/amplifyApiCompat';
import { ConsoleLogger } from 'aws-amplify/utils';
import {FormikValues} from "formik";
import {FormikHelpers} from "formik/dist/types";
import moment from "moment";
import React from "react";
import {Card} from "react-bootstrap";
import * as yup from "yup";
import {DataAudit} from "../../CommonApi/DataAudit";
import {EventType, FrontendModule} from "../../CommonApi/Enum";
import {UsageTracking} from "../../CommonApi/UsageTracking";
import {ApiCallStatus} from "../../CommonFunc/Enum";
import {DataImportFormSubmissionStatus, HtmlForm, RowOfFormFields} from "../Common/HtmlForm";
import {View} from "../GenGenPlate/Enum";
import {gppApiConsts} from "./GppApiConsts";
import {v4 as uuidv4} from "uuid";

const logger = new ConsoleLogger("InHouseCellLineForm.tsx")

export const TABLE_NAME: string = "in_house_cell_line"

export interface InHouseCellLine {
    id: string,
    cellLine: string,
    vendor: string,
    purchaseDate: string,
    annualLicenseFee: boolean,
    availabilityOfInHouseCas9Lines: boolean,
    cas9Version: string,
    availabilityOfInHouseCrisprEditedKnockoutLines: string,
    crisprEditedGeneKnockoutLinesGenerated: string,
    availabilityOfInHouseRnpLines: string,
    rnpEditedGeneKnockoutLinesGenerated: string,
    cellAuthentication: boolean,
    cellAuthenticationDate: string,
    basalMedia: string,
    fbsPercentage: number,
    sodiumPyruvatePercentage: number,
    antibioticsAntimycoticsPercentage: number,
    lGlutaminePercentage: number,
    neaaPercentage: number,
    otherMedia: string,
    additionalNote: string,
    // owner: string --> TODO - check with Yoga if we need to keep track of who creating this and add warning for update?
}

interface InHouseCellLineFormProps {
    displayHeader: boolean
    initialValues: InHouseCellLine
    autofillOwnerByLogInId: boolean
    view: View
}

interface InHouseCellLineFormState {
    rowsOfFormFields: RowOfFormFields[]
    schema: any
    initialValues: InHouseCellLine
    submissionStatus: DataImportFormSubmissionStatus
}

export class InHouseCellLineForm extends React.Component<InHouseCellLineFormProps, InHouseCellLineFormState> {
    static defaultProps = {
        displayHeader: true,
        initialValues: null,
        autofillOwnerByLogInId: true,
    }

    constructor(props: any) {
        // Constructor get call everytime we press update button from TableDataAPIWithUpdateModal
        super(props);
        let initialValuesFromProps = props.initialValues
        if (initialValuesFromProps) {
            initialValuesFromProps = {
                ...initialValuesFromProps,
                purchaseDate: moment(new Date(initialValuesFromProps.purchaseDate)).format("YYYY-MM-DD"),
                cellAuthenticationDate: moment(new Date(initialValuesFromProps.cellAuthenticationDate)).format("YYYY-MM-DD")
            }
        }

        this.state = {
            rowsOfFormFields: [
                {
                    colSize: 4,
                    fields: [
                        {name: "cellLine", dataType: "text", disable: false},
                        {name: "vendor", dataType: "text", disable: false},
                        {name: "purchaseDate", dataType: "date", disable: false},
                        {name: "annualLicenseFee", dataType: "select", disable: false},
                        {name: "availabilityOfInHouseCas9Lines", dataType: "select", disable: true},
                        {name: "cas9Version", dataType: "multi-select", displayNameForMultiSelect: "Cas9 Version", multiSelectValues: [
                            {
                                options: [
                                    {value: "mCherry", label: "mCherry"},
                                    {value: "Blasticidin", label: "Blasticidin"},
                                    {value: "Zeocin", label: "Zeocin"},
                                    {value: "eGFP", label: "eGFP"},
                                    {value: "RFP", label: "RFP"},
                                    {value: "Neomycin", label: "Neomycin"},
                                    {value: "Puromycin", label: "Puromycin"},
                                ],
                            }
                        ], disable: false},
                        {name: "availabilityOfInHouseCrisprEditedKnockoutLines", dataType: "select", disable: false},
                    ]
                },
                {
                    colSize: 12,
                    fields: [
                        {name: "crisprEditedGeneKnockoutLinesGenerated", dataType: "text", disable: false},
                    ]
                },
                {
                    colSize: 4,
                    fields: [
                        {name: "availabilityOfInHouseRnpLines", dataType: "select", disable: false},
                    ]
                },
                {
                    colSize: 12,
                    fields: [
                        {name: "rnpEditedGeneKnockoutLinesGenerated", dataType: "text", disable: false},
                    ]
                },
                {
                    colSize: 4,
                    fields: [
                        {name: "cellAuthentication", dataType: "select", disable: false},
                        {name: "cellAuthenticationDate", dataType: "date", disable: false},
                        {name: "basalMedia", dataType: "select", disable: false},
                        {name: "fbsPercentage", dataType: "number", disable: false},
                        {name: "sodiumPyruvatePercentage", dataType: "number", disable: false},
                        {name: "antibioticsAntimycoticsPercentage", dataType: "number", disable: false},
                        {name: "lGlutaminePercentage", dataType: "number", disable: false},
                        {name: "neaaPercentage", dataType: "number", disable: false},
                    ]
                },
                {
                    colSize: 12,
                    fields: [
                        {name: "otherMedia", dataType: "text", disable: false},
                        {name: "additionalNote", dataType: "text", disable: false},
                    ]
                }
            ],
            schema: yup.object({
                id: yup.string(),
                // Regex match with string-string-string, or string --> we right now didn't check if first string match backbone name.
                cellLine: yup.string().matches(/(^[A-Z0-9.-]+$)/i,
                    "Must only contains uppercase letters, number, . or -").required(),
                vendor: yup.string().required(),
                purchaseDate: yup.string(),
                annualLicenseFee: yup.string(),
                availabilityOfInHouseCas9Lines: yup.string().required(),
                // Commented this out to fix problem of "cas9Version must be a `array` type, but the final value was: `xxx,xxx,xxx`"
                // cas9Version: yup.array(),
                availabilityOfInHouseCrisprEditedKnockoutLines: yup.string().required(),
                crisprEditedGeneKnockoutLinesGenerated: yup.string().matches(/^[A-Z0-9,]+$/i, "Only alphanumerical and comma is allowed"),
                // CRISPR Edited Gene Knockout Lines Generated 
                availabilityOfInHouseRnpLines: yup.string().required(),
                rnpEditedGeneKnockoutLinesGenerated: yup.string().matches(/^[A-Z0-9,]+$/i, "Only alphanumerical and comma is allowed"),
                cellAuthentication: yup.string().required(),
                cellAuthenticationDate: yup.string(),
                basalMedia: yup.string().required(),
                fbsPercentage: yup.number().min(0).max(100).required(),
                sodiumPyruvatePercentage: yup.number().min(0).max(100).required(),
                antibioticsAntimycoticsPercentage: yup.number().min(0).max(100).required(),
                lGlutaminePercentage: yup.number().min(0).max(100).required(),
                neaaPercentage: yup.number().min(0).max(100).required(),
                otherMedia: yup.string().matches(/^[A-Za-z0-9%. \-]+$/i, "Must only contains: alphanumerical % . -"),
                additionalNote: yup.string(),
            }),
            initialValues: initialValuesFromProps === null ? {
                id: "",
                cellLine: "",
                vendor: "",
                purchaseDate: "",
                annualLicenseFee: "",
                availabilityOfInHouseCas9Lines: "",
                cas9Version: "",
                availabilityOfInHouseCrisprEditedKnockoutLines: "",
                crisprEditedGeneKnockoutLinesGenerated: "",
                availabilityOfInHouseRnpLines: "",
                rnpEditedGeneKnockoutLinesGenerated: "",
                cellAuthentication: "",
                cellAuthenticationDate: "",
                basalMedia: "",
                fbsPercentage: "",
                sodiumPyruvatePercentage: "",
                antibioticsAntimycoticsPercentage: "",
                lGlutaminePercentage: "",
                neaaPercentage: "",
                otherMedia: "",
                additionalNote: "",
            } : initialValuesFromProps,
            submissionStatus: {
                status: ApiCallStatus.Loading,
                message: ""
            }
        }
    }

    onSubmit(values: any, formikHelpers: FormikHelpers<FormikValues>): void | Promise<any> {
        this.setState({
            submissionStatus: {
                status: ApiCallStatus.Loading,
                message: ""
            }
        }, () => {
            logger.info("Enter inside onSubmit, values = ", values)

            // This only happened in Firefox, not in Chrome
            if (values["purchaseDate"].toLowerCase() === "invalid date") {
                values["purchaseDate"] = ""
            }
            if (values["cellAuthenticationDate"].toLowerCase() === "invalid date") {
                values["cellAuthenticationDate"] = ""
            }

            logger.info("values = ", values)
            if (values["availabilityOfInHouseCas9Lines"].toLowerCase() === "yes") {
                if (values["cas9Version"].length === 0) {
                    this.setState({
                            submissionStatus: {
                                status: ApiCallStatus.Error,
                                message: 'If "Availability Of In House Cas9 Lines" is "Yes", you must specify "Cas9 Version"',
                            }
                        },
                        () => formikHelpers.setSubmitting(false))
                    return
                }
                
            } else if (values["cas9Version"].length > 0) {
                this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: 'If "Availability Of In House Cas9 Lines" is "No", "Cas9 Version" must be empty',
                        }
                    },
                    () => formikHelpers.setSubmitting(false))
                return
            }
            if (Array.isArray(values["cas9Version"])) {
                values["cas9Version"] = values["cas9Version"].join(",")
            } 

            if (values["availabilityOfInHouseCrisprEditedKnockoutLines"].toLowerCase() === "yes" && values["crisprEditedGeneKnockoutLinesGenerated"] === "") {
                this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: 'If "Availability Of In House Crispr Edited Knockout Lines" is "Yes", you must specify "Crispr Edited Gene Knockout Lines Generated"',
                        }
                    },
                    () => formikHelpers.setSubmitting(false))
                return
            }

            if (values["availabilityOfInHouseRnpLines"].toLowerCase() === "yes" && values["rnpEditedGeneKnockoutLinesGenerated"] === "") {
                this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: 'If "Availability Of In House RNP Lines" is "Yes", you must specify "RNP Edited Gene Knockout Lines Generated"',
                        }
                    },
                    () => formikHelpers.setSubmitting(false))
                return
            }

            // If cell Auth is yes then cell auth date is required
            if (values["cellAuthentication"].toLowerCase() === "yes" && values["cellAuthenticationDate"] === "") {
                this.setState({
                    submissionStatus: {
                        status: ApiCallStatus.Error,
                        message: 'If "Cell Authentication" is "Yes", you must specify "Cell Authentication Date"',
                    }
                },
                    () => formikHelpers.setSubmitting(false))
                return
            }

            // If basal_media === "Other" the rest is optional
            if (values["basalMedia"].toLowerCase() === "other" && values["otherMedia"] === "") {
                this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: 'If "Basel Media" is "Other", "Other Media" cannot be empty',
                        }
                    },
                    () => formikHelpers.setSubmitting(false))
                return
            }

            logger.info("onSubmit - after uppercase conversion - values = ", values)
            formikHelpers.setSubmitting(true)

            if (this.props.view === View.Import) {
                this.apiPut(values, formikHelpers)
            } else if (this.props.view === View.Update) {
                this.apiPatch(values, formikHelpers)
            }
        })
    }

    apiPut(values: any, formikHelpers: FormikHelpers<FormikValues>) {
        UsageTracking.recordEvent({
            module: FrontendModule.DataCatalog,
            event: EventType.Import,
            table: TABLE_NAME,
            database: gppApiConsts.DATABASE_NAME,
            keywords: "", // Comma seperated keywords is splited to seperate Cloudwatch log at backend.
        })
        values["id"] = uuidv4()
        API.put(
            gppApiConsts.API_NAME,
            gppApiConsts.tableData.path.TABLE_DATA,
            {
                body: {
                    table: TABLE_NAME,
                    data: values,
                },
            }).then(response => {
            DataAudit.recordEvent({
                database: gppApiConsts.DATABASE_NAME,
                table: TABLE_NAME,
                rowId: values.id,
                updateNote: "",
                dataObject: values,
            })
            logger.debug("Response from API", response)
            this.setState({
                submissionStatus: {
                    status: ApiCallStatus.Completed,
                    message: values.id + " submitted successfully"
                }
            })
        }).catch(err => {
            logger.error(err)
            if (err.response) {
                if (err.response.statusCode >= 500 || err.response.statusCode < 400) {
                    this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: "Something wrong, please try again"
                        }
                    })
                } else {
                    // Need to JSON parse err.response.body 
                    // {"data":null,"message":"cannot find any guide with sequence = AATTAATTAAAATTAATTAA","requestId":"c5ee5662-f9c2-4837-9c9f-0cfb4bf14ea7"} 
                    // to object before getting message
                    const body = JSON.parse(err.response.body)
                    this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: body.message
                        }
                    })
                }
            }
        }).finally(() => {
            formikHelpers.setSubmitting(false)
            this.setState({
                initialValues: values,
            })
        })
    }

    apiPatch(values: any, formikHelpers: FormikHelpers<FormikValues>) {
        UsageTracking.recordEvent({
            module: FrontendModule.DataCatalog,
            event: EventType.Update,
            table: TABLE_NAME,
            database: gppApiConsts.DATABASE_NAME,
            keywords: values["updateNote"], // Comma seperated keywords is splited to seperate Cloudwatch log at backend.
        })
        API.patch(
            gppApiConsts.API_NAME,
            gppApiConsts.tableData.path.TABLE_DATA,
            {
                body: {
                    table: TABLE_NAME,
                    data: values,
                },
            }).then(response => {
            DataAudit.recordEvent({
                database: gppApiConsts.DATABASE_NAME,
                table: TABLE_NAME,
                rowId: values.id,
                updateNote: values["updateNote"],
                dataObject: values,
            })
            logger.debug("Response from API", response)
            this.setState({
                submissionStatus: {
                    status: ApiCallStatus.Completed,
                    message: "Updated successfully! Record id is " + values["id"]
                }
            })
        }).catch(err => {
            logger.error(err)
            if (err.response) {
                if (err.response.statusCode >= 500 || err.response.statusCode < 400) {
                    this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: "Something wrong, please try again"
                        }
                    })
                } else {
                    // Need to JSON parse err.response.body 
                    // {"data":null,"message":"cannot find any guide with sequence = AATTAATTAAAATTAATTAA","requestId":"c5ee5662-f9c2-4837-9c9f-0cfb4bf14ea7"} 
                    // to object before getting message
                    const body = JSON.parse(err.response.body)
                    this.setState({
                        submissionStatus: {
                            status: ApiCallStatus.Error,
                            message: body.message
                        }
                    })
                }
            }
        }).finally(() => {
            formikHelpers.setSubmitting(false)
            this.setState({
                initialValues: values,
            })
        })
    }

    render() {
        logger.info("state.initialvalues = ", this.state.initialValues)
        return <Card bg={"light"}>
            {this.props.displayHeader && <Card.Header><b>Add new in-house cell line</b></Card.Header>}
            <Card.Body>
                <HtmlForm
                    databaseName={gppApiConsts.DATABASE_NAME}
                    tableName={TABLE_NAME}
                    initialValues={this.state.initialValues}
                    schema={this.state.schema}
                    rowsOfFormFields={this.state.rowsOfFormFields}
                    onSubmit={this.onSubmit.bind(this)}
                    submissionStatus={this.state.submissionStatus}
                    autofillOwnerByLogInId={this.props.autofillOwnerByLogInId}
                    view={this.props.view}
                />
            </Card.Body>
        </Card>
    }
}